import { css } from '@emotion/react';
import {
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  useCallback,
} from 'react';
import { IconName, PlainIcon, ProColors } from '../../../aura';
import { FoundationColors } from '../../../theme';

type CardProps = PropsWithChildren & {
  icon: IconName;
  text: string | ReactElement;
  selected: boolean;
  onClick: () => void;
  padding: number;
};

type CardInternalProps = CardProps & {
  flexDirection: 'row' | 'column';
  gap: number;
};

function getBorderColor(selected: boolean) {
  if (selected) {
    return FoundationColors.blue[600];
  }
  return ProColors.navy[400];
}

function getIconColor(selected: boolean) {
  if (selected) {
    return FoundationColors.blue[600];
  }
  return ProColors.navy[600];
}

function getIconBackgroundColor(selected: boolean) {
  if (selected) {
    return FoundationColors.blue[100];
  }
  return ProColors.navy[200];
}

function CardInternal(props: CardInternalProps): JSX.Element {
  const {
    icon,
    text,
    selected,
    onClick,
    padding,
    flexDirection,
    gap,
    children,
  } = props;

  const wrappedOnClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      onClick();
      e.currentTarget.blur();
    },
    [onClick],
  );

  return (
    <button
      className="aura--card"
      onClick={wrappedOnClick}
      css={css({
        position: 'relative',
        width: '100%',
        height: '100%',
        padding,
        display: 'flex',
        alignItems: flexDirection === 'row' ? 'center' : 'flex-start',
        flexDirection,
        gap,
        borderColor: getBorderColor(selected),
        cursor: 'pointer',
        '&:hover .self-signup--card-vertical-icon': {
          backgroundColor: getIconBackgroundColor(true),
          '& path': {
            fill: getIconColor(true),
          },
        },
      })}
    >
      <PlainIcon
        className="self-signup--card-vertical-icon"
        size={16}
        name={icon}
        color={getIconColor(selected)}
        backgroundColor={getIconBackgroundColor(selected)}
        css={css({ padding: 10 })}
      />
      <div
        className="aura--copy-sm"
        css={css({
          color: ProColors.navy[800],
          fontWeight: 700,
          textAlign: 'start',
        })}
      >
        {text}
      </div>
      {children}
    </button>
  );
}

export function CardHorizontal(props: CardProps): JSX.Element {
  const { icon, text, selected, onClick, padding, children } = props;
  return (
    <CardInternal
      icon={icon}
      text={text}
      selected={selected}
      onClick={onClick}
      flexDirection="row"
      gap={8}
      padding={padding}
      children={children}
    />
  );
}

export function CardVertical(props: CardProps): JSX.Element {
  const { icon, text, selected, onClick, padding, children } = props;
  return (
    <CardInternal
      icon={icon}
      text={text}
      selected={selected}
      onClick={onClick}
      flexDirection="column"
      gap={10}
      padding={padding}
      children={children}
    />
  );
}
